import { useState } from "react";

/**
 * Identifies the underlying asset, ignoring the query string.
 *
 * Media URLs come back signed and the signature is re-issued on every server response, so the
 * same file arrives as a different string each time a mutation resolves. Comparing paths tells
 * an actual media swap apart from a fresh signature on the same file.
 */
function assetPath(src: string): string {
  try {
    return new URL(src, window.location.origin).pathname;
  } catch {
    return src;
  }
}

/**
 * Holds a media URL steady while it points at the same asset.
 *
 * Handing a `<video>` a new `src` string makes the browser tear down and reload the element,
 * which restarts playback from the first frame. Without this, any mutation that returns the
 * slide — saving a caption style, for instance — would visibly reset a video the author is
 * watching, purely because the URL was signed again.
 */
export function useStableMediaSrc(src: string): string {
  const [stable, setStable] = useState(src);

  // Adjusting state during render rather than in an effect: an effect would let one frame
  // render with the new URL, which is all it takes for the element to reload.
  if (assetPath(stable) !== assetPath(src)) {
    setStable(src);
  }

  return stable;
}
